import { supabase } from '@/lib/supabase';
import { stockService } from './stock';
import type { CartServiceResponse, StockValidationResponse, OrderType } from './cart.types';

export class CartStockService {

  /**
   * 결제 시 재고 소모 (실제 재고 차감)
   * @param cartId 장바구니 ID
   * @param orderType 주문 유형 (RESERVATION_DISCOUNT는 재고 차감하지 않음)
   */
  async consumeCartStock(cartId: string, orderType?: OrderType): Promise<CartServiceResponse> {
    try {
      // 예약할인 주문은 재고를 차감하지 않음 (익일 수령이므로 오늘의 재고를 차감하면 안됨)
      if (orderType === 'RESERVATION_DISCOUNT') {
        console.log('예약할인 주문 - 프론트엔드 재고 차감 건너뜀 (백엔드에서 처리)');
        return { success: true };
      }

      const { data: cartItems } = await supabase
        .from('cart_items')
        .select('product_id, quantity')
        .eq('cart_id', cartId);

      if (!cartItems || cartItems.length === 0) {
        return { success: false, error: '장바구니가 비어있습니다.' };
      }

      // 일반주문과 마감할인 주문만 재고를 차감
      for (const item of cartItems) {
        const consumeResult = await stockService.consumeStock(item.product_id, item.quantity);

        if (!consumeResult.success) {
          console.error('Stock consumption error:', consumeResult.error);
          return { 
            success: false, 
            error: `일부 상품의 재고 차감에 실패했습니다. (${consumeResult.error || 'Unknown error'})` 
          };
        }
      }

      return { success: true };
    } catch (error) {
      console.error('Error consuming cart stock:', error);
      return { success: false, error: '재고 차감 중 오류가 발생했습니다.' };
    }
  }

  /**
   * 재고 복구 (결제 실패 시 차감된 재고를 복구)
   * @param cartId 장바구니 ID
   * @param orderType 주문 유형 (RESERVATION_DISCOUNT는 재고 복구하지 않음)
   */
  async restoreCartStock(cartId: string, orderType?: OrderType): Promise<CartServiceResponse> {
    try {
      // 예약할인 주문은 원래 재고를 차감하지 않았으므로 복구도 하지 않음
      if (orderType === 'RESERVATION_DISCOUNT') {
        console.log('예약할인 주문 - 프론트엔드 재고 복구 건너뜀 (원래 차감되지 않음)');
        return { success: true };
      }

      const { data: cartItems } = await supabase
        .from('cart_items')
        .select('product_id, quantity')
        .eq('cart_id', cartId);
      
      if (!cartItems || cartItems.length === 0) {
        return { success: false, error: '장바구니가 비어있습니다.' };
      }

      // 일반주문과 마감할인 주문만 재고를 복구
      for (const item of cartItems) {
        const restoreResult = await stockService.restoreStock(item.product_id, item.quantity);
        if (!restoreResult.success) {
          console.error('Stock restoration error:', restoreResult.error);
          return { 
            success: false, 
            error: `재고 복구에 실패했습니다. (${restoreResult.error || 'Unknown error'})` 
          };
        }
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error restoring cart stock:', error);
      return { success: false, error: '재고 복구 중 오류가 발생했습니다.' };
    }
  }

  /**
   * 최종 결제 전 재고 재검증
   */
  async validateCartStock(cartId: string): Promise<StockValidationResponse> {
    try {
      const { data: cartItems } = await supabase
        .from('cart_items')
        .select(`
          *,
          product:products(id, name)
        `)
        .eq('cart_id', cartId);

      if (!cartItems || cartItems.length === 0) {
        return { success: false, unavailableItems: [], error: '장바구니가 비어있습니다.' };
      }

      const today = new Date().toISOString().split('T')[0];
      const unavailableItems: string[] = [];
      
      // 각 상품의 재고 체크
      for (const item of cartItems) {
        const stockData = await stockService.getProductStock(item.product_id, today, true);

        if (!stockData) {
          unavailableItems.push(item.product?.name || item.product_id);
        } else {
          const currentStock = stockData.current_stock;
          if (currentStock < item.quantity) {
            unavailableItems.push(item.product?.name || item.product_id);
          }
        }
      }

      return { 
        success: unavailableItems.length === 0, 
        unavailableItems 
      };
    } catch (error) {
      console.error('Error validating cart stock:', error);
      return { success: false, unavailableItems: [], error: '재고 검증 중 오류가 발생했습니다.' };
    }
  }
}

export const cartStockService = new CartStockService();